import React from 'react';
import './BankSyncModal.css';
import './BankTransferModal.css';

// Formular + PIN/TAN-Dialog fuer eine SEPA-Ueberweisung. Der PIN/TAN-Teil
// (Zeilen ab handleResult()) ist bewusst fast identisch zu BankSyncModal.js -
// gleicher mehrstufiger FinTS-Dialog (PIN -> ggf. TAN/decoupled-Bestaetigung),
// nur mit BankingTransferSubmit statt BankingSync als Task und einem
// vorgeschalteten Empfaenger-Formular. Siehe dort fuer die Begruendung, warum
// PIN/TAN nur lokal in diesem State leben.
function stripHtml(html) {
	return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export default function BankTransferModal(props) {

	const x = props.x;
	// Nicht auf props.connections verlassen: das ist nur der Stand, den
	// BankTransactions.js beim eigenen Laden EINMALIG abgerufen hat. Wurde ein
	// PIN seitdem gemerkt (auch aus dem Brokerage-Bereich - PinStore ist pro
	// connectionid, nicht pro Modul), waere das pinstored-Flag hier veraltet.
	// Deshalb selbst frisch laden, analog zu BankSyncAllModal.js.
	const [rawConnections, setRawConnections] = React.useState(props.connections || null);
	const connections = (rawConnections || []).filter((c) => parseInt(c.disabled, 10) !== 1);

	React.useEffect(() => {
		x.was.axGet('Backend/webapp.php?module=Banking&task=BankingConnectionList', (result) => {
			if (result.data.returncode === 1) setRawConnections(result.data.content);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Vorbelegung aus einer angeklickten Buchung (BankTransactionDetailModal ->
	// "Neue Ueberweisung") - Empfaenger/IBAN/Betrag/Zweck/Quellkonto werden nur
	// vorausgefuellt, der Nutzer sieht/bestaetigt sie weiterhin im normalen
	// Formular, bevor irgendetwas abgeschickt wird.
	const prefill = props.prefill || null;

	const [connectionId, setConnectionId] = React.useState(
		prefill?.connectionId || (connections.length > 0 ? connections[0].connectionid : '')
	);

	// Erste Verbindung nachtraeglich vorauswaehlen, sobald die frisch
	// geladene Liste eintrifft (beim ersten Render war rawConnections evtl.
	// noch null/veraltet, siehe Kommentar oben). Eine per prefill vorgegebene
	// Verbindung hat Vorrang und wird nicht ueberschrieben.
	React.useEffect(() => {
		if (connectionId === '' && !prefill?.connectionId && connections.length > 0) {
			setConnectionId(connections[0].connectionid);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rawConnections]);

	const [recipientName, setRecipientName] = React.useState(prefill?.recipientName || '');
	const [recipientIban, setRecipientIban] = React.useState(prefill?.recipientIban || '');
	const [recipientBic, setRecipientBic] = React.useState('');
	const [amount, setAmount] = React.useState(prefill?.amount || '');
	const [purpose, setPurpose] = React.useState(prefill?.purpose || '');
	const [formError, setFormError] = React.useState('');
	const [submitted, setSubmitted] = React.useState(false);

	const [pin, setPin] = React.useState('');
	const [remember, setRemember] = React.useState(false);
	const [tanCode, setTanCode] = React.useState('');
	const [fintsState, setFintsState] = React.useState('');
	const [challenge, setChallenge] = React.useState(null);
	const [busy, setBusy] = React.useState(false);
	const [error, setError] = React.useState('');
	const [hint, setHint] = React.useState('');
	const [usingStored, setUsingStored] = React.useState(false);
	const [decoupled, setDecoupled] = React.useState(null);
	// Verification of Payee (VOP): Bank gleicht Empfaengername mit dem
	// Kontoinhaber ab. vopPolling = Abgleich laeuft noch (wird automatisch
	// weiterverfolgt). vopConfirmation = Ergebnis liegt vor und muss vom
	// Nutzer bestaetigt werden, bevor es weitergeht (v.a. bei Abweichung).
	const [vopPolling, setVopPolling] = React.useState(null);
	const [vopConfirmation, setVopConfirmation] = React.useState(null);
	// null = Schalterzustand noch nicht geladen; solange kein Auto-Abruf mit
	// gemerktem PIN, siehe BankSyncAllModal.js fuer dasselbe Muster.
	const [pinUse, setPinUse] = React.useState(null);
	const pollTimer = React.useRef(null);

	const connection = connections.find((c) => c.connectionid === connectionId) || null;
	const pinStored = connection ? parseInt(connection.pinstored, 10) === 1 : false;

	React.useEffect(() => {
		x.was.axPost('Backend/webapp.php?module=Banking&task=BankingPinSwitch', {}, (result) => {
			setPinUse(result.data.returncode === 1 && parseInt(result.data.content.enabled, 10) === 1);
		});
		return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function close() {
		if (pollTimer.current) clearTimeout(pollTimer.current);
		props.onClose();
	}

	function validateForm() {
		if (connectionId === '') return 'Bitte ein Konto auswählen.';
		if (recipientName.trim() === '') return 'Bitte den Namen des Empfängers angeben.';
		const iban = recipientIban.replace(/\s/g, '').toUpperCase();
		if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(iban)) return 'IBAN sieht nicht gültig aus.';
		// BIC ist seit der SEPA-IBAN-only-Regel fuer Inlandsueberweisungen
		// optional - nur validieren, wenn ueberhaupt etwas eingegeben wurde.
		const bic = recipientBic.replace(/\s/g, '').toUpperCase();
		if (bic !== '' && !/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic)) return 'BIC sieht nicht gültig aus.';
		const amountValue = parseFloat(amount.replace(',', '.'));
		if (!(amountValue > 0)) return 'Bitte einen Betrag größer 0 angeben.';
		return '';
	}

	function submitForm() {
		const validationError = validateForm();
		if (validationError !== '') {
			setFormError(validationError);
			return;
		}
		setFormError('');
		setSubmitted(true);
		if (pinUse === true && pinStored) {
			setUsingStored(true);
			setBusy(true);
			postTransfer({ pin: '' });
		}
	}

	function postTransfer(extra) {
		x.was.axPost('Backend/webapp.php?module=Banking&task=BankingTransferSubmit', {
			connectionId: connectionId,
			recipientName: recipientName.trim(),
			recipientIban: recipientIban.replace(/\s/g, '').toUpperCase(),
			recipientBic: recipientBic.replace(/\s/g, '').toUpperCase(),
			amount: amount.replace(',', '.'),
			purpose: purpose.trim(),
			...extra,
		}, handleResult);
	}

	function pollDecoupled(state, delaySeconds, checksLeft) {
		pollTimer.current = setTimeout(() => {
			x.was.axPost('Backend/webapp.php?module=Banking&task=BankingTransferSubmit', {
				connectionId: connectionId,
				pin: pin,
				checkDecoupled: 1,
				fintsState: state,
			}, (result) => handleResult(result, checksLeft - 1));
		}, delaySeconds * 1000);
	}

	function pollVopStatus(state, delaySeconds) {
		pollTimer.current = setTimeout(() => {
			x.was.axPost('Backend/webapp.php?module=Banking&task=BankingTransferSubmit', {
				connectionId: connectionId,
				pin: pin,
				pollVop: 1,
				fintsState: state,
			}, handleResult);
		}, delaySeconds * 1000);
	}

	function confirmVopAndContinue() {
		setBusy(true);
		setError('');
		x.was.axPost('Backend/webapp.php?module=Banking&task=BankingTransferSubmit', {
			connectionId: connectionId,
			pin: pin,
			confirmVop: 1,
			fintsState: vopConfirmation.fintsState,
		}, handleResult);
	}

	function submitPin() {
		if (pin === '') return;
		setBusy(true);
		setError('');
		postTransfer({ pin: pin, rememberPin: remember ? 1 : 0 });
	}

	function submitTan() {
		if (tanCode === '') return;
		setBusy(true);
		setError('');
		x.was.axPost('Backend/webapp.php?module=Banking&task=BankingTransferSubmit', {
			connectionId: connectionId,
			pin: pin,
			rememberPin: (pin !== '' && remember) ? 1 : 0,
			tanCode: tanCode,
			fintsState: fintsState,
		}, handleResult);
	}

	function handleResult(result, checksLeft) {
		const content = result.data.content || {};
		if (result.data.returncode !== 1) {
			setBusy(false);
			setDecoupled(null);
			setVopPolling(null);
			setVopConfirmation(null);
			if (content.storedPinCleared) {
				setUsingStored(false);
				setHint('Der gemerkte PIN wurde abgelehnt und verworfen – bitte neu eingeben.');
				setError(content.message || '');
				return;
			}
			if (content.status === 'pin-required') {
				setUsingStored(false);
				return;
			}
			setError(content.message || 'Überweisung fehlgeschlagen.');
			return;
		}
		if (content.status === 'vop-polling') {
			setVopPolling(content.message || 'Empfängerprüfung läuft…');
			setVopConfirmation(null);
			pollVopStatus(content.fintsState, content.pollDelaySeconds || 3);
			return;
		}
		if (content.status === 'vop-confirmation-required') {
			setBusy(false);
			setVopPolling(null);
			setVopConfirmation({
				verificationResult: content.verificationResult,
				informationForUser: content.informationForUser,
				verificationNotApplicableReason: content.verificationNotApplicableReason,
				fintsState: content.fintsState,
			});
			return;
		}
		if (content.status === 'tan-required') {
			setBusy(false);
			setDecoupled(null);
			setVopPolling(null);
			setVopConfirmation(null);
			setChallenge({ text: content.challenge, tanMediumName: content.tanMediumName, image: content.challengeImage });
			setFintsState(content.fintsState);
			setTanCode('');
			return;
		}
		if (content.status === 'tan-decoupled') {
			setVopPolling(null);
			setVopConfirmation(null);
			setChallenge({ text: content.challenge, tanMediumName: content.tanMediumName, image: content.challengeImage });
			setFintsState(content.fintsState);
			const remaining = (typeof checksLeft === 'number') ? checksLeft : content.maxChecks;
			if (content.maxChecks > 0 && remaining <= 0) {
				setBusy(false);
				setDecoupled(null);
				setError('Bestätigung im Zweitgerät nicht rechtzeitig erhalten. Bitte erneut versuchen.');
				return;
			}
			setDecoupled(true);
			const delay = (typeof checksLeft === 'number') ? content.periodicCheckDelaySeconds : content.firstCheckDelaySeconds;
			pollDecoupled(content.fintsState, delay, remaining);
			return;
		}
		setBusy(false);
		setDecoupled(null);
		setVopPolling(null);
		setVopConfirmation(null);
		x.wah.addHint('Überweisung an ' + recipientName.trim() + ' über ' + amount.replace(',', '.') + ' € wurde ausgeführt.');
		props.onSuccess();
	}

	return (
		<div className="banking-sync-overlay">
			<div className="banking-sync-modal banking-transfer-modal">
				<div className="banking-sync-header">
					<span>Überweisung</span>
					<i className="bi bi-x-lg table-icon" onClick={close}></i>
				</div>

				{!submitted &&
					<div className="banking-sync-body">
						<label htmlFor="banking-transfer-connection">Von Konto</label>
						<select
							id="banking-transfer-connection"
							value={connectionId}
							onChange={(e) => setConnectionId(parseInt(e.target.value, 10))}
						>
							{connections.map((c) => (
								<option key={c.connectionid} value={c.connectionid}>{c.bankname} – {c.accountlabel}</option>
							))}
						</select>

						<label htmlFor="banking-transfer-name">Empfänger</label>
						<input
							id="banking-transfer-name"
							type="text"
							value={recipientName}
							onChange={(e) => setRecipientName(e.target.value)}
							autoFocus
						/>

						<label htmlFor="banking-transfer-iban">IBAN</label>
						<input
							id="banking-transfer-iban"
							type="text"
							autoComplete="off"
							value={recipientIban}
							onChange={(e) => setRecipientIban(e.target.value)}
						/>

						<label htmlFor="banking-transfer-bic">BIC (optional)</label>
						<input
							id="banking-transfer-bic"
							type="text"
							autoComplete="off"
							placeholder="bei Inlandsüberweisung nicht nötig"
							value={recipientBic}
							onChange={(e) => setRecipientBic(e.target.value)}
						/>

						<label htmlFor="banking-transfer-amount">Betrag (€)</label>
						<input
							id="banking-transfer-amount"
							type="text"
							inputMode="decimal"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
						/>

						<label htmlFor="banking-transfer-purpose">Verwendungszweck</label>
						<input
							id="banking-transfer-purpose"
							type="text"
							value={purpose}
							onChange={(e) => setPurpose(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Enter') submitForm(); }}
						/>

						{formError !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {formError}</p>}
						<button type="button" className="btn" onClick={submitForm}>Weiter</button>
					</div>
				}

				{submitted && challenge === null && usingStored &&
					<div className="banking-sync-body">
						<p><i className="bi bi-key"></i> Verwende gemerkten PIN…</p>
						{error !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
					</div>
				}

				{submitted && challenge === null && !usingStored &&
					<div className="banking-sync-body">
						{hint !== '' && <p className="banking-sync-hint"><i className="bi bi-info-circle"></i> {hint}</p>}
						<label htmlFor="banking-transfer-pin">PIN</label>
						<input
							id="banking-transfer-pin"
							className="brokerage-pin-input"
							type="text"
							inputMode="numeric"
							autoComplete="one-time-code"
							value={pin}
							onChange={(e) => setPin(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Enter') submitPin(); }}
							autoFocus
						/>
						{pinUse === true &&
							<label className="banking-sync-remember">
								<input
									type="checkbox"
									checked={remember}
									onChange={(e) => setRemember(e.target.checked)}
								/>
								PIN merken (verschlüsselt, nur dieser Browser)
							</label>
						}
						{error !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
						<button type="button" className="btn" disabled={busy || pin === ''} onClick={submitPin}>
							{busy ? 'Sende…' : 'Überweisung ausführen'}
						</button>
					</div>
				}

				{submitted && vopPolling !== null &&
					<div className="banking-sync-body">
						<p><i className="bi bi-hourglass-split"></i> {vopPolling}</p>
					</div>
				}

				{submitted && vopConfirmation !== null &&
					<div className="banking-sync-body">
						<p className="banking-vop-result">
							{vopConfirmation.verificationResult === 'CompletedFullMatch' && 'Name stimmt mit dem Kontoinhaber überein.'}
							{vopConfirmation.verificationResult === 'CompletedCloseMatch' && 'Name stimmt nur teilweise mit dem Kontoinhaber überein.'}
							{vopConfirmation.verificationResult === 'CompletedNoMatch' && 'Name stimmt NICHT mit dem Kontoinhaber überein.'}
							{vopConfirmation.verificationResult === 'CompletedPartialMatch' && 'Name stimmt nur teilweise mit dem Kontoinhaber überein.'}
							{vopConfirmation.verificationResult === 'NotApplicable' && 'Empfängerprüfung war nicht möglich.'}
						</p>
						{vopConfirmation.informationForUser &&
							// informationForUser kommt laut Bibliotheks-Doku als
							// HTML-formatierter Text von der Bank - wird hier bewusst
							// NICHT per dangerouslySetInnerHTML gerendert (XSS-Risiko
							// bei bankgeliefertem Markup), sondern als Klartext mit
							// entfernten Tags.
							<p className="banking-sync-hint">{stripHtml(vopConfirmation.informationForUser)}</p>
						}
						{vopConfirmation.verificationNotApplicableReason &&
							<p className="banking-sync-hint">{vopConfirmation.verificationNotApplicableReason}</p>
						}
						{vopConfirmation.verificationResult !== 'CompletedFullMatch' &&
							<p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> Bitte den Empfänger prüfen, bevor du fortfährst.</p>
						}
						{error !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
						<div className="banking-sync-all-actions">
							<button type="button" className="btn" disabled={busy} onClick={confirmVopAndContinue}>
								{busy ? 'Sende…' : 'Trotzdem überweisen'}
							</button>
							<button type="button" className="btn" disabled={busy} onClick={close}>Abbrechen</button>
						</div>
					</div>
				}

				{submitted && challenge !== null && decoupled &&
					<div className="banking-sync-body">
						{challenge.text && <p className="banking-sync-challenge">{challenge.text}</p>}
						{challenge.image && <img className="banking-sync-challenge-image" src={challenge.image} alt="TAN-Grafik zum Scannen" />}
						{challenge.tanMediumName && <p className="banking-sync-medium">Gerät: {challenge.tanMediumName}</p>}
						<p><i className="bi bi-phone"></i> Bitte in der Banking-App bestätigen…</p>
						{error !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
					</div>
				}

				{submitted && challenge !== null && !decoupled &&
					<div className="banking-sync-body">
						{challenge.text && <p className="banking-sync-challenge">{challenge.text}</p>}
						{challenge.image && <img className="banking-sync-challenge-image" src={challenge.image} alt="TAN-Grafik zum Scannen" />}
						{challenge.tanMediumName && <p className="banking-sync-medium">Gerät: {challenge.tanMediumName}</p>}
						<label htmlFor="banking-transfer-tan">TAN</label>
						<input
							id="banking-transfer-tan"
							type="text"
							autoComplete="off"
							value={tanCode}
							onChange={(e) => setTanCode(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Enter') submitTan(); }}
							autoFocus
						/>
						{error !== '' && <p className="banking-sync-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
						<button type="button" className="btn" disabled={busy || tanCode === ''} onClick={submitTan}>
							{busy ? 'Bestätige…' : 'TAN bestätigen'}
						</button>
					</div>
				}
			</div>
		</div>
	)

};
